import { useSnackbar } from "notistack";
import { useRef } from "react";
import { useGetIsMounted } from "rooks";
import { IncomingOptions, useFetch } from "use-http";

import { attemptSessionRefresh } from "api/sessionRefresh/refresh";
import { getIsLoggingOut, setIsLoggingOut } from "api/utils";
import { REACT_APP_LOGOUT_URL } from "config/config";
import { AUTH_REDIRECT_URL, MAX_RETRIES, RETRY_DELAY } from "config/constants";
import { usePostEvent } from "hooks/usePostEvent";
import { handleApiError } from "utils/apiErrorHandling";
import { getCookie } from "utils/cookies";
import { captureAuthErrorInSentry } from "utils/sentryErrorTracking";
import { isErrorStatusCode, isSuccessfulStatusCode, isTimeoutStatusCode } from "utils/statusCheck";

type CustomIncomingOptions = IncomingOptions & {
  shouldShowErrorSnackbar?: (response: Response) => boolean;
};

/**
 * Wraps use-http with shared Groundlight auth refresh, retry, and error handling behavior.
 * This hook is legacy; prefer the React Query APIs backed by fetchWithRefresh for new code.
 */
export function useFetchCustom<T = any>(url?: string, options?: CustomIncomingOptions, dependencies?: any[]) {
  const { enqueueSnackbar } = useSnackbar();
  const { sendErrorEvent } = usePostEvent();
  const getIsMounted = useGetIsMounted();
  // Tracks when each request attempt began so the refresh dedup check has a meaningful anchor.
  const requestStartedAtRef = useRef<number>(0);
  // Ensures a silent refresh is attempted at most once per request, regardless of which retry
  // attempt surfaces the 401 (e.g. a 401 that follows a 5xx retry). Prevents infinite loops.
  const refreshAttemptedRef = useRef<boolean>(false);

  const dataResponse = useFetch<T>(
    url,
    {
      retries: process.env.NODE_ENV === "test" ? 0 : MAX_RETRIES,
      retryOn: async ({ attempt, response }) => {
        if (attempt === 0) {
          requestStartedAtRef.current = Date.now();
          refreshAttemptedRef.current = false;
        }
        if (getIsLoggingOut()) return false;

        if (!response) {
          return false;
        }

        const canSeeSnackbarError =
          options?.shouldShowErrorSnackbar === undefined || options.shouldShowErrorSnackbar(response);
        if (isSuccessfulStatusCode(response.status)) {
          return false; // Success response, no need to retry
        }

        // If the user is not authenticated, redirect to login page.
        // We convert "failed to fetch" into 419 using the sw.js file.
        // We get "failed to fetch" after a 302 to cognito and CORS failure.
        if (response.status === 419) {
          setIsLoggingOut();
          captureAuthErrorInSentry(response, AUTH_REDIRECT_URL);
          enqueueSnackbar("Your session has expired. Redirecting you to the login page ...", { variant: "info" });
          window.location.href = AUTH_REDIRECT_URL;
          return false;
        }

        // On 401 the access token expired or was revoked. Attempt a silent token refresh at most
        // once per request (tracked via refreshAttemptedRef, so a 401 following a 5xx retry still
        // triggers it). If the refresh succeeds, retry the request. If a refresh was already
        // attempted or it fails, fall through to the login redirect rather than looping.
        if (response.status === 401) {
          if (!refreshAttemptedRef.current) {
            refreshAttemptedRef.current = true;
            if (await attemptSessionRefresh(requestStartedAtRef.current)) {
              return true;
            }
          }
          const authRedirectUrl = response.headers.get('X-Auth-Redirect');
          const redirectTarget = authRedirectUrl || REACT_APP_LOGOUT_URL;
          setIsLoggingOut();
          captureAuthErrorInSentry(response, redirectTarget);
          if (authRedirectUrl) {
            enqueueSnackbar("Your session has expired. Redirecting you to the login page ...", { variant: "info" });
          }
          window.location.href = redirectTarget;
          return false;
        }

        if (isErrorStatusCode(response.status)) {
          if (!getIsMounted()) {
            return false;
          }
          if (canSeeSnackbarError) {
            handleApiError(response, enqueueSnackbar, {
              shouldShowSnackbar: true,
              isRetry: false,
            });
          }
          return false;
        }

        if (isTimeoutStatusCode(response.status)) {
          if (!getIsMounted()) {
            return false;
          }

          if (attempt === MAX_RETRIES) {
            if (canSeeSnackbarError) {
              handleApiError(response, enqueueSnackbar, {
                shouldShowSnackbar: true,
                isRetry: true,
                attempt,
                maxRetries: MAX_RETRIES,
              });
            }
            const eventText = `${response.url}`;
            sendErrorEvent(eventText);
          }
          return true; // Retry for server errors
        }

        return false;
      },
      retryDelay: ({ attempt }) => RETRY_DELAY * (attempt + 1),
      headers: { "X-CSRFToken": getCookie("csrftoken") || "" },
      ...options,
    },
    dependencies
  );

  return dataResponse;
}
